import { useIsLoggedIn } from '@order/AuthMachine';
import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useSweetpassTier } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

// ─────────────────────────────────────────────────────────────────────────────

export const useSweetpassUpsellCtaContentType = (
  params: UseSweetpassUpsellCtaContentTypeParams,
) => {
  const { contentTag = 'sweetpassUpsellCtaDefault', contentFallback } = params;

  const { t } = useLocalizationContext();
  const { sweetpassTierName } = useSweetpassTier('cache-only');

  const isLoggedIn = useIsLoggedIn();
  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled');
  const isUpgradableTier = sweetpassTierName === 'SWEETPASS';

  const shouldPauseQuery =
    !isSweetpassEnabled || (isLoggedIn && !isUpgradableTier);

  // ─────────────────────────────────────────────

  const fallbackValues: SweetpassUpsellCtaContentType = contentFallback ?? {
    text: t('sweetpass.upsell.cta.fallback.text'),
    textHighlights: [t('sweetpass.upsell.cta.fallback.text-highlight')],
    actionText: t('sweetpass.upsell.cta.fallback.action-text'),
    iconText: t('sweetpass.upsell.cta.fallback.icon-text'),
    accessibilityLabel: t('sweetpass.upsell.cta.fallback.a11y-label'),
  };

  const { data, fetching } =
    useContentfulContentTypeEntry<SweetpassUpsellCtaContentType>({
      contentType: 'sweetpassUpsellCta',
      tags: [contentTag],
      pause: shouldPauseQuery,
    });

  const fields = data?.fields ?? fallbackValues;

  // ─────────────────────────────────────────────

  return { fields, fetching };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSweetpassUpsellCtaContentTypeParams = {
  readonly contentTag?: SweetpassUpsellCtaContentTypeTag;
  readonly contentFallback?: SweetpassUpsellCtaContentType;
};

type SweetpassUpsellCtaContentType = {
  readonly text: string;
  readonly textHighlights?: readonly string[];
  readonly actionText?: string;
  readonly iconText?: string;
  readonly accessibilityLabel?: string;
  readonly accessibilityHint?: string;
};

type SweetpassUpsellCtaContentTypeTag =
  | 'sweetpassUpsellCtaDefault'
  | 'sweetpassUpsellCtaActiveOrder'
  | 'sweetpassUpsellCtaOrderStatus';
